<template>
  <div class="campo-busca-container mb-3">
    <label
      v-if="labelExibido && !hideLabel"
      :for="id"
      class="form-label"
      :class="{ 'is-required': isObrigatorio }"
    >
      {{ labelExibido }}
      <i
        v-if="labelInfo"
        class="bi bi-info-circle text-primary ms-1"
        :title="labelInfo"
        style="cursor: help; font-size: 0.85em;"
      ></i>
    </label>

    <div class="input-group has-validation">
      <input
        :id="id"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': errorMessage && !hasValidDescription }"
        :value="textoExibicao"
        @focus="isFocused = true"
        @blur="handleBlur"
        @input="onInput"
        @keydown="onKeydownAutocomplete"
        @keydown.enter="onEnterPressionado"
        v-bind="$attrs"
        :placeholder="placeholderExibido"
      >
      <button
        class="btn btn-outline-secondary d-flex align-items-center"
        type="button"
        @click="abrirModalDeBusca"
        :class="{ 'border-danger text-danger': errorMessage && !hasValidDescription }"
        :disabled="isDisabled"
      >
        <i class="bi bi-search"></i>
      </button>
      <button
        v-if="textoExibicao && !isDisabled"
        class="btn btn-outline-secondary d-flex align-items-center"
        type="button"
        @click="limparValor"
        title="Limpar seleção"
      >
        <i class="bi bi-x-lg"></i>
      </button>

      <div v-if="errorMessage && !hasValidDescription" class="invalid-feedback">
        {{ errorMessage }}
      </div>
    </div>

    <div v-if="hasValidDescription || mensagemNaoEncontrado" class="form-text mt-1 shadow-sm px-2 py-1 rounded bg-body-tertiary border border-secondary-subtle d-inline-block text-body-emphasis">
      <template v-if="mensagemNaoEncontrado">
        <i class="bi bi-exclamation-triangle me-1 text-warning"></i> {{ mensagemNaoEncontrado }}
      </template>
      <template v-else>
        <i class="bi bi-info-circle me-1 text-primary"></i> {{ descricao }}
      </template>
    </div>

    <!-- 🆕 Dropdown de autocomplete -->
    <div
      v-if="dropdownVisivel"
      class="autocomplete-dropdown"
    >
      <div
        v-for="(item, idx) in resultadosDropdown"
        :key="idx"
        class="autocomplete-item"
        :class="{ 'is-hover': idx === indiceSelecionado }"
        @mousedown.prevent="selecionarItem(item)"
        @mouseenter="indiceSelecionado = idx"
      >
        <div class="autocomplete-item-primary">
          <i class="bi bi-dot me-1"></i>
          <span class="fw-medium">{{ item[camposExibicaoAutocomplete[0]?.dataField] }}</span>
        </div>
        <div v-if="camposSecundariosAutocomplete.length > 0" class="autocomplete-item-secondary">
          <span
            v-for="col in camposSecundariosAutocomplete"
            :key="col.dataField"
            class="autocomplete-tag"
          >
            {{ item[col.dataField] }}
          </span>
        </div>
      </div>
      <div v-if="loadingAutocomplete" class="autocomplete-empty">
        <span class="spinner-border spinner-border-sm me-1"></span> Buscando...
      </div>
      <div v-else-if="resultadosDropdown.length === 0" class="autocomplete-empty">
        <i class="bi bi-search me-1"></i> Nenhuma opção correspondente.
      </div>
    </div>

    <ModalDeBusca
      v-if="exibirModal"
      :configuracao="configuracaoBusca"
      :z-index-base="zIndexModalBusca"
      :itens-ja-selecionados="ultimoItemSelecionado ? [ultimoItemSelecionado] : []"
      :on-show-notification="(payload) => { console.log('[DEBUG CampoBusca] onShowNotification disparado:', JSON.stringify(payload)); emit('show-notification', payload) }"
      @fechar="fecharModalDeBusca"
      @item-selecionado="onItemSelecionado"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed, inject, useAttrs, reactive } from 'vue'
import api from '@/services/api'
import ModalDeBusca from '../modal/ModalDeBusca.vue'
import { useMetadataListasStore } from '@/stores/metadataListas'

const attrs = useAttrs()

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  modelValue: { type: [Number, String], default: null },
  configuracaoBusca: { type: Object, required: true },
  required: { type: [Boolean, String, Number], default: false },
  hideLabel: { type: Boolean, default: false },
  error: { type: String, default: '' },
  zIndexModalBusca: { type: Number, default: undefined },
  labelInfo: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue', 'selecionado', 'show-notification', 'cancelar-busca'])

const metadataStore = useMetadataListasStore()
const listasCache = reactive({})

const descricao = ref('')
const exibirModal = ref(false)
const isFocused = ref(false)
const erroInterno = ref('')
const textoExibicao = ref('')
const mensagemNaoEncontrado = ref('')
const modeloAnterior = ref(null)
/** Objeto completo do último item selecionado, para repassar ao ModalDeBusca via itensJaSelecionados */
const ultimoItemSelecionado = ref(null)

// 🆕 Variáveis de estado do autocomplete
const dropdownVisivel = ref(false)
const resultadosDropdown = ref([])
const loadingAutocomplete = ref(false)
const indiceSelecionado = ref(-1)
let autocompleteController = null
let debounceAutocompleteTimer = null
const cacheAutocomplete = {}
const chavesCache = []
const CACHE_MAX = 30

const formErrors = inject('formErrors', ref({}))
const errorMessage = computed(() => props.error || erroInterno.value || formErrors.value[props.id] || '')
const hasValidDescription = computed(() => !!(descricao.value && !erroInterno.value))

const conf = computed(() => props.configuracaoBusca || {})
const labelExibido = computed(() => props.label || conf.value.label || '')
const placeholderExibido = computed(() => conf.value.placeholder || 'ID ou Código...')
const temDisplayField = computed(() => !!conf.value.displayField)

/** 🆕 Verifica se o bloco autocomplete está habilitado na configuração */
const autocompleteHabilitado = computed(() => {
  const ac = conf.value.autocomplete
  return ac && ac.habilitado !== false
})

/** 🆕 Campos a exibir no dropdown de autocomplete (usa camposExibicao ou colunasGrid) */
const camposExibicaoAutocomplete = computed(() => {
  return conf.value.autocomplete?.camposExibicao || conf.value.colunasGrid || []
})

/** 🆕 Campos secundários (pula o primeiro, que é o primary) */
const camposSecundariosAutocomplete = computed(() => {
  return camposExibicaoAutocomplete.value.slice(1)
})

/** Serializa filtrosFixos da configuracaoBusca para params de busca.
 *  Arrays são convertidos para string separada por vírgula (formato compatível com o backend).
 */
const filtrosFixosParams = computed(() => {
  const fixos = conf.value.filtrosFixos
  if (!fixos) return {}
  const params = {}
  for (const [key, value] of Object.entries(fixos)) {
    if (value === null || value === undefined) continue
    params[key] = Array.isArray(value) ? value.join(',') : value
  }
  return params
})

/**
 * Valida se um item obtido por busca direta atende aos filtrosFixos configurados.
 * Percorre cada chave de filtrosFixos e compara com a propriedade correspondente no item.
 *
 * Regras de comparação:
 * - Se o valor do filtro for um array: item[key] deve estar contido no array
 * - Se o valor do filtro for string com vírgulas: trata como array (split por vírgula)
 * - Caso contrário: comparação direta via String() para tolerar diferenças de tipo
 *
 * IMPORTANTE: chaves de filtrosFixos que NÃO existem como propriedade no item são
 * ignoradas (ex: sortField, sortOrder, pageSize — parâmetros de query que não são
 * dados da entidade). Apenas chaves presentes no item são efetivamente validadas.
 *
 * @param {object} item - Item retornado da API
 * @returns {boolean} - true se o item atende a todos os filtros (ou não há filtros configurados)
 */
const itemAtendeFiltrosFixos = (item) => {
  const fixos = conf.value.filtrosFixos
  if (!fixos || !item || typeof item !== 'object') return true

  for (const [key, filtroValor] of Object.entries(fixos)) {
    if (filtroValor === null || filtroValor === undefined) continue

    const itemValor = item[key]
    // Chave ausente no item (parâmetro de query, ex: sortField/sortOrder) → ignora
    if (itemValor === undefined || itemValor === null) continue

    // Array: verifica se o valor do item está contido no array
    if (Array.isArray(filtroValor)) {
      if (!filtroValor.some(v => String(v) === String(itemValor))) return false
      continue
    }

    // String com vírgulas: trata como array (formato compatível com a serialização do backend)
    if (typeof filtroValor === 'string' && filtroValor.includes(',')) {
      const valores = filtroValor.split(',').map(v => v.trim())
      if (!valores.some(v => String(v) === String(itemValor))) return false
      continue
    }

    // Comparação direta via String() para tolerar Number vs String
    if (String(filtroValor) !== String(itemValor)) return false
  }

  return true
}

const isDisabled = computed(() => {
  return attrs.disabled === true || attrs.disabled === '' || attrs.disabled === 'disabled' || attrs.disabled === 'true'
})

const isObrigatorio = computed(() => {
  return props.required === true || props.required === 'true' || props.required === 'required' || props.required === ''
})

const onInput = (event) => {
  textoExibicao.value = event.target.value || ''

  if (autocompleteHabilitado.value) {
    onInputAutocomplete(event)
    return
  }

  // Comportamento original
  mensagemNaoEncontrado.value = ''
  erroInterno.value = ''
  descricao.value = ''
  ultimoItemSelecionado.value = null
  emit('update:modelValue', null)
}

/**
 * Resolve automaticamente campos Lst* nos dados retornados.
 * Varre as propriedades do objeto, identifica campos que começam com "Lst"/"lst",
 * consulta o DicionarioMetadadosListas e popula {campo}Texto com o textoLista.
 * @param {object} item Objeto retornado da API
 */
const resolverListasNoItem = async (item) => {
  const tabela = conf.value.tabela
  if (!tabela || !item || typeof item !== 'object') return

  await metadataStore.carregarMetadados()

  for (const key of Object.keys(item)) {
    if (!/^[lL]st[A-Z]/.test(key)) continue

    const valor = item[key]
    if (valor == null || valor === '') continue

    const cacheKey = `${tabela}.${key}`
    if (!listasCache[cacheKey]) {
      const lista = await metadataStore.getListaPorCampo(tabela, key)
      listasCache[cacheKey] = lista || []
    }

    const lista = listasCache[cacheKey]
    const itemLista = lista.find(i => String(i.idListaClienteItem) === String(valor))
    if (itemLista) {
      item[`${key}Texto`] = itemLista.textoLista
    }
  }
}

/**
 * Resolve o descField a partir da config, com fallback para colunasGrid.
 * Se o campo original é Lst*, prioriza o campo resolvido {campo}Texto.
 */
const resolverDescField = (item) => {
  const campoOriginal = conf.value.descField || conf.value.colunasGrid?.find(c =>
    c.header?.toLowerCase().includes('nome') ||
    c.header?.toLowerCase().includes('desc') ||
    c.header?.toLowerCase().includes('banco') ||
    c.dataField === 'textoLista'
  )?.dataField
  if (campoOriginal && /^[lL]st[A-Z]/.test(campoOriginal) && item[`${campoOriginal}Texto`]) {
    return `${campoOriginal}Texto`
  }
  return campoOriginal
}

/** 🆕 Gerencia a entrada de texto quando autocomplete está habilitado */
const onInputAutocomplete = (event) => {
  const termo = event.target.value.trim()
  const ac = conf.value.autocomplete

  mensagemNaoEncontrado.value = ''
  erroInterno.value = ''
  descricao.value = ''
  ultimoItemSelecionado.value = null
  emit('update:modelValue', null)

  if (termo.length < (ac?.minChars || 3)) {
    dropdownVisivel.value = false
    loadingAutocomplete.value = false
    resultadosDropdown.value = []
    clearTimeout(debounceAutocompleteTimer)
    return
  }

  // Cache hit: exibe imediatamente, inclusive quando vazio (para mostrar
  // "Nenhuma opção correspondente.").
  const cacheKey = termo.toLowerCase().trim()
  if (cacheAutocomplete[cacheKey]) {
    resultadosDropdown.value = cacheAutocomplete[cacheKey]
    dropdownVisivel.value = true
    loadingAutocomplete.value = false
    clearTimeout(debounceAutocompleteTimer)
    return
  }

  clearTimeout(debounceAutocompleteTimer)
  // Exibe o dropdown com "Buscando..." já durante o debounce
  loadingAutocomplete.value = true
  dropdownVisivel.value = true
  resultadosDropdown.value = []
  debounceAutocompleteTimer = setTimeout(() => {
    buscarAutocomplete(termo)
  }, ac?.debounceMs || 350)
}

/** 🆕 Busca resultados na API para autocomplete */
const buscarAutocomplete = async (termo) => {
  if (autocompleteController) autocompleteController.abort()
  const controller = new AbortController()
  autocompleteController = controller
  const ac = conf.value.autocomplete

  loadingAutocomplete.value = true
  dropdownVisivel.value = true

  try {
    const response = await api.get(`/${conf.value.endpointApi}`, {
      params: {
        [ac?.campoBusca || 'busca']: termo,
        pageSize: ac?.maxResults || 15,
        ...filtrosFixosParams.value
      },
      signal: controller.signal,
      _suppressLoading: true
    })

    const resultados = response.data || []
    await resolverListasEmLote(resultados)

    // Ignora resposta de requisição obsoleta (já substituída por outra)
    if (controller !== autocompleteController) return

    // Cache LRU
    const cacheKey = termo.toLowerCase().trim()
    if (chavesCache.length >= CACHE_MAX) {
      const antiga = chavesCache.shift()
      delete cacheAutocomplete[antiga]
    }
    chavesCache.push(cacheKey)
    cacheAutocomplete[cacheKey] = resultados

    resultadosDropdown.value = resultados
    // Mantém o dropdown visível mesmo sem resultados, para exibir a
    // mensagem "Nenhuma opção correspondente."
    dropdownVisivel.value = true
  } catch (error) {
    if (error.name === 'AbortError') return
    if (controller !== autocompleteController) return
    resultadosDropdown.value = []
    dropdownVisivel.value = false
  } finally {
    // Só encerra o loading se ainda for a requisição corrente
    if (controller === autocompleteController) {
      loadingAutocomplete.value = false
    }
  }
}

/** 🆕 Seleciona um item do dropdown */
const selecionarItem = async (item) => {
  dropdownVisivel.value = false
  loadingAutocomplete.value = false
  indiceSelecionado.value = -1
  clearTimeout(debounceAutocompleteTimer)

  await resolverListasNoItem(item)
  const descField = resolverDescField(item)

  textoExibicao.value = conf.value.displayField
    ? (item[conf.value.displayField] ?? '')
    : (descField && item[descField] ? item[descField] : item[conf.value.idField])

  descricao.value = conf.value.descFormatter
    ? conf.value.descFormatter(item)
    : ((descField && item[descField]) ? item[descField] : 'Registro identificado')

  modeloAnterior.value = item[conf.value.idField]
  ultimoItemSelecionado.value = item

  emit('update:modelValue', item[conf.value.idField])
  emit('selecionado', item)
  mensagemNaoEncontrado.value = ''
  erroInterno.value = ''
}

/** 🆕 Navegação por teclado no dropdown */
const onKeydownAutocomplete = (event) => {
  if (!dropdownVisivel.value || resultadosDropdown.value.length === 0) return

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    indiceSelecionado.value = Math.min(indiceSelecionado.value + 1, resultadosDropdown.value.length - 1)
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    indiceSelecionado.value = Math.max(indiceSelecionado.value - 1, 0)
  } else if (event.key === 'Enter' && indiceSelecionado.value >= 0) {
    event.preventDefault()
    selecionarItem(resultadosDropdown.value[indiceSelecionado.value])
  } else if (event.key === 'Escape') {
    dropdownVisivel.value = false
    indiceSelecionado.value = -1
  }
}

/** 🆕 Resolve campos Lst* em lote para array de itens */
const resolverListasEmLote = async (itens) => {
  if (!Array.isArray(itens)) return
  for (const item of itens) {
    await resolverListasNoItem(item)
  }
}

/**
  * Busca o item usando o displayField como termo de busca.
 * Usa endpointFetchPorCodigo se disponível, senão GetAll com ?busca=termo.
 * Sempre busca textualmente — o ID nunca aparece no input quando displayField está configurado.
 */
const buscarPeloDisplayField = async () => {
  const termo = textoExibicao.value
  if (!termo) {
    descricao.value = ''
    return
  }

  try {
    let response
    let item = null

    if (conf.value.endpointFetchPorCodigo) {
      response = await api.get(`/${conf.value.endpointFetchPorCodigo}/${encodeURIComponent(termo)}`, { params: filtrosFixosParams.value })
      item = response.data
      // Valida se o item obtido por código atende aos filtrosFixos
      if (item && !itemAtendeFiltrosFixos(item)) {
        item = null
      }
    } else {
      // Se o termo parece numérico (ID), tenta buscar por ID primeiro
      const isNumeric = typeof termo === 'string' && !isNaN(Number(termo))

      if (isNumeric) {
        try {
          response = await api.get(`/${conf.value.endpointApi}/${termo}`)
          item = response.data
          // Valida se o item obtido por ID direto atende aos filtrosFixos
          if (item && !itemAtendeFiltrosFixos(item)) {
            item = null
          }
        } catch {
          item = null
        }
      }

      if (!item) {
        response = await api.get(`/${conf.value.endpointApi}`, {
          params: { busca: termo, pageSize: 1, ...filtrosFixosParams.value }
        })
        item = Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null
      }
    }

    if (item) {
      await resolverListasNoItem(item)

      const descField = resolverDescField(item)

      textoExibicao.value = conf.value.displayField ? (item[conf.value.displayField] ?? termo) : termo
      descricao.value = conf.value.descFormatter
        ? conf.value.descFormatter(item)
        : ((descField && item[descField]) ? item[descField] : 'Registro identificado')

      erroInterno.value = ''
      mensagemNaoEncontrado.value = ''
      ultimoItemSelecionado.value = item
      if (conf.value.idField) emit('update:modelValue', item[conf.value.idField])
      emit('selecionado', item)
    } else {
      throw new Error('Not found')
    }
  } catch (error) {
    erroInterno.value = 'Código não localizado ou inválido.'
    mensagemNaoEncontrado.value = `Valor "${termo}" não encontrado.`
    descricao.value = ''
    emit('update:modelValue', null)
    const mensagem = error?.response?.data?.message ||
      error?.response?.data?.title ||
      'O código informado não é válido.'
    emit('show-notification', { message: mensagem, type: 'error' })
  }
}

/**
 * Busca legada por ID (usada quando NÃO há displayField configurado).
 * Tenta adivinhar se o termo é numérico (ID) ou textual (código).
 */
const buscarDescricaoPeloId = async () => {
  const termo = textoExibicao.value
  if (!termo) {
    descricao.value = ''
    return
  }

  try {
    let endpointFinal = ''
    let usarBuscaTexto = false

    if (conf.value.endpointFetchPorCodigo) {
      // endpointFetchPorCodigo configurado: sempre use-o para resolver o termo,
      // mesmo que seja numérico (ex: NCM "85171431" não é ID, é código)
      endpointFinal = `${conf.value.endpointFetchPorCodigo}/${termo}`
    } else if (typeof termo === 'string' && isNaN(Number(termo))) {
      usarBuscaTexto = true
    } else {
      endpointFinal = conf.value.endpointFetchId
        ? `${conf.value.endpointFetchId}/${termo}`
        : `${conf.value.endpointApi}/${termo}`
    }

    let response
    if (usarBuscaTexto) {
      response = await api.get(`/${conf.value.endpointApi}`, {
        params: { busca: termo, pageSize: 1, ...filtrosFixosParams.value }
      })
    } else {
      response = await api.get(`/${endpointFinal}`, { params: filtrosFixosParams.value })
    }

    const rawData = response.data
    const item = usarBuscaTexto
      ? (Array.isArray(rawData) && rawData.length > 0 ? rawData[0] : null)
      : rawData

    // Valida se o item obtido por ID direto atende aos filtrosFixos
    if (item && !usarBuscaTexto && !itemAtendeFiltrosFixos(item)) {
      throw new Error('Not found - filter mismatch')
    }

    if (item) {
      await resolverListasNoItem(item)

      const descField = resolverDescField(item)

      const campoExibicao = conf.value.displayField
        ? item[conf.value.displayField]
        : null
      textoExibicao.value = campoExibicao || termo

      descricao.value = conf.value.descFormatter
        ? conf.value.descFormatter(item)
        : ((descField && item[descField]) ? item[descField] : 'Registro identificado')

      erroInterno.value = ''
      mensagemNaoEncontrado.value = ''
      ultimoItemSelecionado.value = item
      if (conf.value.idField) emit('update:modelValue', item[conf.value.idField])
      emit('selecionado', item)
    } else {
      throw new Error('Not found')
    }
  } catch (error) {
    erroInterno.value = 'Código não localizado ou inválido.'
    mensagemNaoEncontrado.value = `Valor "${termo}" não encontrado.`
    descricao.value = ''
    emit('update:modelValue', null)
    const mensagem = error?.response?.data?.message ||
      error?.response?.data?.title ||
      'O código informado não é válido.'
    emit('show-notification', { message: mensagem, type: 'error' })
  }
}

/**
 * Gerencia a prioridade do Enter entre CampoBusca e ModalFormulario.
 * - Se NÃO tem item válido (pendente/erro): previne o evento e faz a busca.
 * - Se TEM item válido: deixa o Enter passar para o ModalFormulario salvar.
 *
 * NOTA: Quando autocomplete está habilitado, o Enter com dropdown visível
 * é tratado pelo onKeydownAutocomplete (seleção do item destacado).
 * Se o dropdown está visível mas nenhum item foi destacado, apenas fecha
 * o dropdown sem auto-selecionar.
 */
const onEnterPressionado = (event) => {
  if (hasValidDescription.value) {
    return
  }

  // Se autocomplete está ativo e dropdown visível, não auto-seleciona
  if (autocompleteHabilitado.value && dropdownVisivel.value) {
    event.preventDefault()
    dropdownVisivel.value = false
    indiceSelecionado.value = -1
    return
  }

  event.preventDefault()
  if (temDisplayField.value) {
    buscarPeloDisplayField()
  } else {
    buscarDescricaoPeloId()
  }
}

const handleBlur = () => {
  isFocused.value = false
  // Fecha dropdown com delay para permitir clique no item
  setTimeout(() => {
    dropdownVisivel.value = false
    indiceSelecionado.value = -1
  }, 200)

  // Se autocomplete está habilitado, não auto-seleciona no blur
  // O usuário deve escolher explicitamente no dropdown
  if (autocompleteHabilitado.value) {
    debounceCancel()
    return
  }

  if (textoExibicao.value && !descricao.value) {
    if (temDisplayField.value) {
      buscarPeloDisplayField()
    } else {
      buscarDescricaoPeloId()
    }
  }
  debounceCancel()
}

const debounceTimer = ref(null)

const debounceBuscar = () => {
  debounceCancel()
  debounceTimer.value = setTimeout(() => {
    if (props.modelValue && !descricao.value) {
      if (temDisplayField.value) {
        buscarPeloDisplayField()
      } else {
        buscarDescricaoPeloId()
      }
    }
  }, 300)
}

const debounceCancel = () => {
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
    debounceTimer.value = null
  }
}

onMounted(async () => {
  modeloAnterior.value = props.modelValue
  if (props.modelValue && temDisplayField.value) {
    // Resolve o item pelo ID (ou código, quando o endpointApi aceita código) para obter o displayField.
    // O ID JAMAIS aparece no input.
    try {
      const response = await api.get(`/${conf.value.endpointFetchId || conf.value.endpointApi}/${props.modelValue}`, { params: filtrosFixosParams.value })
      const item = response.data
      // Valida se o item obtido por ID direto atende aos filtrosFixos
      if (item && !itemAtendeFiltrosFixos(item)) {
        throw new Error('Filter mismatch')
      }
      if (item) {
        await resolverListasNoItem(item)
        const descField = resolverDescField(item)
        textoExibicao.value = conf.value.displayField ? (item[conf.value.displayField] ?? '') : ''
        descricao.value = conf.value.descFormatter
          ? conf.value.descFormatter(item)
          : ((descField && item[descField]) ? item[descField] : 'Registro identificado')
        ultimoItemSelecionado.value = item
      }
    } catch (e) {
      // Fallback: tenta pelo displayField (busca textual)
      await buscarPeloDisplayField()
    }
  } else if (props.modelValue) {
    // Sem displayField: comportamento legado (exibe o ID temporariamente)
    textoExibicao.value = String(props.modelValue)
    buscarDescricaoPeloId()
  }
})

watch(() => props.modelValue, (novoId) => {
  if (!novoId) {
    if (!isFocused.value) {
      textoExibicao.value = ''
    }
    descricao.value = ''
    mensagemNaoEncontrado.value = ''
    debounceCancel()
    modeloAnterior.value = null
    return
  }

  // Se o ID mudou para um valor diferente do anterior, limpa a descrição
  // para forçar a re-resolução do item (ex: alternar entre registros)
  if (modeloAnterior.value !== null && modeloAnterior.value !== novoId) {
    descricao.value = ''
    textoExibicao.value = ''
    mensagemNaoEncontrado.value = ''
    erroInterno.value = ''
  }
  modeloAnterior.value = novoId

  if (!descricao.value) {
    if (temDisplayField.value) {
      // Tenta resolver pelo ID (ou código, quando o endpointApi aceita código) para obter o displayField
      api.get(`/${conf.value.endpointFetchId || conf.value.endpointApi}/${novoId}`, { params: filtrosFixosParams.value })
        .then(response => {
          const item = response.data
          // Valida se o item obtido por ID direto atende aos filtrosFixos
          if (item && !itemAtendeFiltrosFixos(item)) {
            throw new Error('Filter mismatch')
          }
          if (item) {
            resolverListasNoItem(item).then(() => {
              const descField = resolverDescField(item)
              textoExibicao.value = conf.value.displayField ? (item[conf.value.displayField] ?? '') : ''
              descricao.value = conf.value.descFormatter
                ? conf.value.descFormatter(item)
                : ((descField && item[descField]) ? item[descField] : 'Registro identificado')
              ultimoItemSelecionado.value = item
            })
          }
        })
        .catch(() => {
          buscarPeloDisplayField()
        })
    } else {
      textoExibicao.value = String(novoId)
      debounceBuscar()
    }
  }
})

const abrirModalDeBusca = () => { exibirModal.value = true }

const limparValor = () => {
  textoExibicao.value = ''
  descricao.value = ''
  mensagemNaoEncontrado.value = ''
  erroInterno.value = ''
  ultimoItemSelecionado.value = null
  modeloAnterior.value = null
  dropdownVisivel.value = false
  resultadosDropdown.value = []
  indiceSelecionado.value = -1
  debounceCancel()
  emit('update:modelValue', null)
}
const fecharModalDeBusca = () => {
  exibirModal.value = false
  emit('cancelar-busca')
}

const onItemSelecionado = async (item) => {
  await resolverListasNoItem(item)

  const descField = resolverDescField(item)

  // displayField é a fonte autoritativa para o texto do input
  textoExibicao.value = conf.value.displayField
    ? (item[conf.value.displayField] ?? '')
    : (descField && item[descField] ? item[descField] : item[conf.value.idField])

  descricao.value = conf.value.descFormatter
    ? conf.value.descFormatter(item)
    : ((descField && item[descField]) ? item[descField] : 'Registro identificado')

  // Sincroniza modeloAnterior ANTES do emit para evitar que o watch
  // de modelValue limpe os campos (descricao.value, textoExibicao.value)
  modeloAnterior.value = item[conf.value.idField]
  ultimoItemSelecionado.value = item

  emit('update:modelValue', item[conf.value.idField])
  emit('selecionado', item)
  mensagemNaoEncontrado.value = ''
  erroInterno.value = ''
  fecharModalDeBusca()
}

onUnmounted(() => {
  debounceCancel()
})

defineExpose({ abrirModalDeBusca })
</script>

<style scoped>
.form-label { font-weight: 400 !important; }
.form-label.is-required::after { content: " *"; color: #dc3545; font-weight: bold; }
.campo-busca-container {
  display: flex;
  flex-direction: column;
  position: relative;
}

/* 🆕 Estilos do dropdown de autocomplete */
.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1050;
  width: 100%;
  max-height: 280px;
  overflow-y: auto;
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  margin-top: 4px;
}
.autocomplete-item {
  cursor: pointer;
  padding: 8px 12px;
  border-bottom: 1px solid var(--bs-border-color-translucent);
  transition: background 0.1s ease;
}
.autocomplete-item:last-child { border-bottom: none; }
.autocomplete-item.is-hover,
.autocomplete-item:hover {
  background: var(--bs-tertiary-bg);
}
.autocomplete-item-primary {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.9rem;
  color: var(--bs-body-color);
}
.autocomplete-item-primary .bi-dot {
  font-size: 0.6rem;
  color: var(--bs-secondary-color);
}
.autocomplete-item-secondary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}
.autocomplete-tag {
  display: inline-block;
  font-size: 0.75rem;
  padding: 1px 8px;
  background: var(--bs-tertiary-bg);
  border-radius: 10px;
  color: var(--bs-secondary-color);
}
.autocomplete-empty {
  padding: 12px;
  font-size: 0.85rem;
  color: var(--bs-secondary-color);
  text-align: center;
}
</style>
